import React, { useContext, useEffect, useState, useMemo } from 'react';
import TenantContext from '@/contexts/TenantContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  DollarSign, 
  Search, 
  Plus, 
  TrendingUp, 
  CreditCard, 
  Tag, 
  FileText, 
  Trash2, 
  Calendar, 
  Target, 
  Activity,
  User,
  ShoppingBag,
  Percent,
  Wallet,
  Clock,
  ArrowRight,
  MoreVertical,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VentasProPage = () => {
  const { tenantSlug } = useContext(TenantContext);
  const [ventas, setVentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [clientes, setClientes] = useState([]);
  
  // Form State
  const [formData, setFormData] = useState({
    folio: '',
    cliente: '',
    producto_servicio: '',
    cantidad: 1,
    precio_unitario: 0,
    descuento: 0,
    metodo_pago: 'Transferencia',
    estado_pago: 'Completado',
    estado_venta: 'Entregado',
    vendedor: '',
    canal_venta: 'Directo',
    notas: ''
  });

  useEffect(() => {
    fetchVentas();
    fetchClientes();
  }, [tenantSlug]);

  const fetchVentas = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/ventas`);
      setVentas(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar historial de ventas');
      setVentas([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/clientes`);
      setClientes(res.data || []);
    } catch (err) {
      console.error("Error fetching clients for datalist:", err);
    }
  };

  // Automatic Calculations
  const subtotal = useMemo(() => {
    return (parseFloat(formData.cantidad) || 0) * (parseFloat(formData.precio_unitario) || 0);
  }, [formData.cantidad, formData.precio_unitario]);

  const total = useMemo(() => {
    return subtotal - (parseFloat(formData.descuento) || 0);
  }, [subtotal, formData.descuento]);

  const openNewForm = () => {
    const newFolio = `VEN-${Math.floor(1000 + Math.random() * 9000)}`;
    setFormData({
      folio: newFolio,
      cliente: '',
      producto_servicio: '',
      cantidad: 1,
      precio_unitario: 0,
      descuento: 0,
      metodo_pago: 'Transferencia',
      estado_pago: 'Completado',
      estado_venta: 'Entregado',
      vendedor: '',
      canal_venta: 'Directo',
      notas: ''
    });
    setIsSidebarOpen(true);
  };

  const handleSave = async () => {
    if (!formData.producto_servicio || total <= 0) {
      toast.error("Producto y Monto total son obligatorios");
      return;
    }
    
    try {
      const payload = {
        ...formData,
        subtotal,
        total,
        monto: total, // Para compatibilidad con dashboards genéricos
        concepto: formData.producto_servicio, // Alias para compatibilidad
        fecha_registro: new Date().toISOString(),
        fecha_alta: new Date().toISOString()
      };

      await axios.post(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/ventas/inject`, { data: payload });
      toast.success("Transacción registrada con éxito");
      setIsSidebarOpen(false);
      fetchVentas();
    } catch (err) {
      toast.error("Fallo en la inyección de datos al libro mayor");
    }
  };

  const handleDelete = async (recordId) => {
    if(!window.confirm("¿Seguro que deseas anular permanentemente esta venta? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/ventas/${recordId}`);
      toast.success("Registro de venta eliminado");
      fetchVentas();
    } catch (err) {
      toast.error("Error al anular transacción");
    }
  };

  const filteredVentas = ventas.filter(v => 
    v.producto_servicio?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    v.concepto?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.cliente?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.folio?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Financial Stats
  const totalBruto = ventas.reduce((acc, curr) => acc + (parseFloat(curr.total || curr.monto || 0)), 0);
  const ticketPromedio = ventas.length > 0 ? (totalBruto / ventas.length) : 0;
  const ventasHoy = ventas.filter(v => {
    const d = new Date(v.fecha_registro || v.engine_injected_at);
    return d.toDateString() === new Date().toDateString();
  }).length;

  const getStatusColor = (status) => {
    switch((status || '').toUpperCase()) {
      case 'COMPLETADO':
      case 'ENTREGADO': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case 'PENDIENTE':
      case 'PROCESO': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      case 'CANCELADO': return 'bg-rose-500/10 text-rose-400 border-rose-500/20';
      default: return 'bg-slate-800 text-slate-400 border-white/5';
    }
  };

  return (
    <div className="h-full flex flex-col pt-6 lg:pt-0 pb-12">
      <div className="mb-6 relative">
        <div className="absolute inset-0 bg-emerald-500/5 blur-[120px] rounded-full pointer-events-none" />
        <div className="flex flex-col gap-6 relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                <span className="text-[9px] uppercase tracking-[0.4em] font-black text-emerald-400/80">Gestión de Ingresos</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-black tracking-tight text-white font-outfit leading-none">
                Fluxo <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">Ventas</span>
              </h1>
              <p className="text-slate-400 mt-3 font-medium italic text-xs max-w-xl">
                Libro mayor inteligente con sincronización CRM y análisis de rentabilidad.
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3 bg-white/[0.03] border border-white/5 p-2 rounded-[1.5rem] w-full lg:w-auto lg:min-w-[500px]"
            >
                <div className="flex items-center bg-slate-900/50 rounded-xl border border-white/5 px-2">
                  <button 
                    onClick={() => document.getElementById('ventas-table-container').scrollBy({ left: -300, behavior: 'smooth' })}
                    className="p-2 text-slate-600 hover:text-white transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="w-[1px] h-4 bg-white/5 mx-1" />
                  <button 
                    onClick={() => document.getElementById('ventas-table-container').scrollBy({ left: 300, behavior: 'smooth' })}
                    className="p-2 text-slate-600 hover:text-white transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="relative flex-1 group">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input
                    type="text"
                    className="block w-full pl-9 pr-4 py-2.5 bg-slate-950/50 border border-white/10 rounded-xl text-xs placeholder-slate-600 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all font-bold tracking-tight"
                    placeholder="Buscar venta..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                  
                <button 
                  onClick={openNewForm}
                  className="px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg hover:-translate-y-0.5 active:scale-95 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Nueva</span>
                </button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* KPI Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Ingresos Totales', val: `$${totalBruto.toLocaleString()}`, icon: DollarSign, color: 'emerald', trend: '+12% hoy' },
          { label: 'Ticket Promedio', val: `$${ticketPromedio.toLocaleString()}`, icon: Target, color: 'cyan', trend: 'Estable' },
          { label: 'Transacciones', val: ventas.length, icon: ShoppingBag, color: 'blue', trend: `${ventasHoy} nuevas` },
          { label: 'Ratio Conversión', val: '24%', icon: Activity, color: 'purple', trend: '+3% vs ayer' }
        ].map((kpi, i) => (
          <motion.div 
            key={i}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="p-4 rounded-3xl bg-slate-900/40 border border-white/5 relative overflow-hidden group hover:border-emerald-500/20 transition-all duration-500"
          >
            <div className={`absolute right-0 top-0 w-24 h-24 bg-${kpi.color}-500/5 rounded-full -translate-y-10 translate-x-10 blur-2xl group-hover:bg-${kpi.color}-500/10 transition-colors`} />
            <div className="flex items-center justify-between mb-2">
              <div className={`p-2 rounded-xl bg-${kpi.color}-500/10 border border-${kpi.color}-500/20`}>
                <kpi.icon className={`w-4 h-4 text-${kpi.color}-400`} />
              </div>
              <span className={`text-[9px] font-black text-${kpi.color}-400/60 uppercase tracking-tighter`}>{kpi.trend}</span>
            </div>
            <h3 className="text-2xl font-black text-white font-outfit leading-none mb-1">{kpi.val}</h3>
            <p className="text-[9px] font-black text-slate-500 uppercase tracking-[0.2em]">{kpi.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Table Section */}
      <div id="ventas-table-container" className="bg-slate-900/40 backdrop-blur-2xl border border-white/5 rounded-[2rem] flex-1 overflow-hidden flex flex-col shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-white/[0.02] border-b border-white/5">
                  <th className="p-5 text-[10px] font-black uppercase tracking-widest text-slate-500">Transacción</th>
                  <th className="p-5 text-[10px] font-black uppercase tracking-widest text-slate-500">Cliente / Canal</th>
                  <th className="p-5 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">Estado</th>
                  <th className="p-5 text-[10px] font-black uppercase tracking-widest text-slate-500">Monto Final</th>
                  <th className="p-5 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.02]">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="p-12 text-center text-slate-500 font-bold uppercase tracking-[0.3em] text-[10px] animate-pulse">Sincronizando Libro Mayor...</td>
                  </tr>
                ) : filteredVentas.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="p-12 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="p-4 rounded-full bg-white/5 border border-white/10 text-slate-600">
                          <ShoppingBag className="w-8 h-8" />
                        </div>
                        <div>
                          <p className="text-white font-black uppercase tracking-tighter text-lg">Sin Historial</p>
                          <p className="text-slate-500 text-[10px] font-medium max-w-xs mx-auto">No se han detectado transacciones en este tenant. Comienza registrando una nueva venta.</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredVentas.map((v, idx) => (
                    <motion.tr 
                      key={v._id} 
                      initial={{ opacity: 0, x: -10 }} 
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      className="group hover:bg-white/[0.02] transition-colors border-b border-white/[0.02] last:border-0"
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20 group-hover:scale-110 transition-transform">
                            <FileText className="w-4 h-4 text-emerald-400" />
                          </div>
                          <div>
                            <p className="text-white font-black text-[13px] tracking-tight uppercase group-hover:text-emerald-400 transition-colors">{v.producto_servicio || v.concepto || 'Venta'}</p>
                            <p className="text-[9px] text-slate-500 font-black uppercase tracking-widest">{v.folio}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="space-y-0.5">
                          <p className="text-slate-200 font-bold text-[12px] flex items-center gap-1.5">
                            <User className="w-3 h-3 text-blue-400" />
                            {v.cliente || 'Venta Mostrador'}
                          </p>
                          <p className="text-[9px] text-slate-500 font-black uppercase tracking-widest pl-4.5">{v.canal_venta || 'Directo'}</p>
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest border ${getStatusColor(v.estado_pago || 'Pagado')}`}>
                            {v.estado_pago || 'Liquidado'}
                          </span>
                          <span className="text-[8px] font-bold text-slate-600 uppercase tracking-widest">{v.metodo_pago}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col">
                          <span className="text-white font-black text-base font-outfit">${parseFloat(v.total || v.monto || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                          {parseFloat(v.descuento) > 0 && (
                            <span className="text-rose-400/60 text-[9px] font-bold">-{parseFloat(v.descuento).toLocaleString()} dto</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button 
                            onClick={() => handleDelete(v._id)}
                            className="p-2.5 bg-rose-500/5 text-rose-500/40 hover:bg-rose-500 hover:text-white rounded-xl transition-all opacity-0 group-hover:opacity-100 border border-rose-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
      </div>

      {/* Slide-over Form Overlay */}
      <AnimatePresence>
        {isSidebarOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-[#020617]/80 backdrop-blur-md z-[100]"
              onClick={() => setIsSidebarOpen(false)}
            />
            <motion.div 
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 220 }}
              className="fixed top-0 right-0 h-full w-full max-w-[500px] bg-[#0F172A] border-l border-white/10 z-[101] shadow-2xl overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                            <Plus className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-white tracking-tighter uppercase italic">Registrar Venta</h2>
                            <p className="text-slate-500 text-[9px] font-black uppercase tracking-widest mt-0.5 italic">Folio: {formData.folio}</p>
                        </div>
                    </div>
                    <button onClick={() => setIsSidebarOpen(false)} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-500 transition-colors">
                        <Plus className="w-7 h-7 rotate-45" />
                    </button>
                </div>
                
                <div className="space-y-6">
                   {/* Client & Core Info */}
                   <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Cliente Vinculado</label>
                            <div className="relative group">
                                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600 transition-colors group-focus-within:text-emerald-400" />
                                <input 
                                    list="ventas-clientes-list"
                                    type="text" 
                                    className="w-full bg-black/40 border border-white/5 rounded-xl py-2.5 pl-11 pr-4 text-white focus:border-emerald-500/50 outline-none transition-all font-black placeholder:text-slate-800 text-xs italic"
                                    value={formData.cliente}
                                    onChange={e => setFormData({...formData, cliente: e.target.value})}
                                    placeholder="Buscar en CRM..."
                                />
                                <datalist id="ventas-clientes-list">
                                    {clientes.map(c => <option key={c._id} value={c.nombre} />)}
                                </datalist>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Canal de Venta</label>
                            <select 
                                className="w-full bg-black/40 border border-white/5 rounded-xl py-2.5 px-3 text-white focus:border-emerald-500/50 outline-none transition-all font-black appearance-none text-xs uppercase tracking-widest italic"
                                value={formData.canal_venta}
                                onChange={e => setFormData({...formData, canal_venta: e.target.value})}
                            >
                                <option value="Directo">Venta Directa</option>
                                <option value="WhatsApp">WhatsApp / Chat</option>
                                <option value="Web">Portal Web</option>
                                <option value="Referido">Referido</option>
                            </select>
                        </div>
                   </div>

                   <div className="space-y-1.5">
                        <label className="text-[9px] font-black uppercase tracking-widest text-emerald-500 ml-1 italic">Producto o Servicio *</label>
                        <div className="relative group">
                            <ShoppingBag className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500/50 transition-colors group-focus-within:text-emerald-400" />
                            <input 
                                type="text" 
                                className="w-full bg-emerald-500/[0.02] border border-emerald-500/20 rounded-xl py-2.5 pl-11 pr-4 text-white focus:border-emerald-500/50 outline-none transition-all font-black placeholder:text-slate-800 text-xs italic"
                                value={formData.producto_servicio}
                                onChange={e => setFormData({...formData, producto_servicio: e.target.value})}
                                placeholder="Describa el concepto de venta..."
                            />
                        </div>
                   </div>

                   {/* Pricing Engine */}
                   <div className="p-6 rounded-[1.5rem] bg-black/40 border border-white/5 space-y-5">
                        <div className="grid grid-cols-2 gap-5">
                            <div className="space-y-1.5">
                                <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Cantidad</label>
                                <input 
                                    type="number" 
                                    className="w-full bg-slate-900/50 border border-white/5 rounded-xl py-2.5 px-3 text-white focus:border-emerald-500/50 outline-none transition-all font-black text-xs italic"
                                    value={formData.cantidad}
                                    onChange={e => setFormData({...formData, cantidad: e.target.value})}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Precio Unitario ($)</label>
                                <div className="relative">
                                    <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600" />
                                    <input 
                                        type="number" 
                                        className="w-full bg-slate-900/50 border border-white/5 rounded-xl py-2.5 pl-10 pr-4 text-white focus:border-emerald-500/50 outline-none transition-all font-black text-xs italic"
                                        value={formData.precio_unitario}
                                        onChange={e => setFormData({...formData, precio_unitario: e.target.value})}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-5">
                            <div className="space-y-1.5">
                                <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Descuento ($)</label>
                                <div className="relative">
                                    <Percent className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-rose-500/50" />
                                    <input 
                                        type="number" 
                                        className="w-full bg-slate-900/50 border border-white/5 rounded-xl py-2.5 pl-10 pr-4 text-rose-400 focus:border-rose-500/50 outline-none transition-all font-black text-xs italic"
                                        value={formData.descuento}
                                        onChange={e => setFormData({...formData, descuento: e.target.value})}
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Subtotal (Auto)</label>
                                <div className="py-2.5 px-3 rounded-xl bg-slate-800/30 border border-white/5 text-slate-400 font-black text-sm italic">
                                    ${subtotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                </div>
                            </div>
                        </div>

                        <div className="pt-3 border-t border-white/5 flex items-center justify-between">
                            <span className="text-[10px] font-black text-white uppercase tracking-widest italic">Total Final</span>
                            <span className="text-xl font-black text-emerald-400 font-outfit">
                                ${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </span>
                        </div>
                   </div>

                   {/* Payment & Status */}
                   <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Método Pago</label>
                            <div className="relative">
                                <Wallet className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600 pointer-events-none" />
                                <select 
                                    className="w-full bg-black/40 border border-white/5 rounded-xl py-2.5 pl-11 pr-4 text-white focus:border-emerald-500/50 outline-none transition-all font-black appearance-none text-xs uppercase italic tracking-widest"
                                    value={formData.metodo_pago}
                                    onChange={e => setFormData({...formData, metodo_pago: e.target.value})}
                                >
                                    <option value="Transferencia">Transferencia</option>
                                    <option value="Efectivo">Efectivo</option>
                                    <option value="Tarjeta">Tarjeta</option>
                                    <option value="Crypto">Crypto</option>
                                </select>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Estado Pago</label>
                            <select 
                                className="w-full bg-black/40 border border-white/5 rounded-xl py-2.5 px-3 text-white focus:border-emerald-500/50 outline-none transition-all font-black appearance-none text-xs uppercase italic tracking-widest"
                                value={formData.estado_pago}
                                onChange={e => setFormData({...formData, estado_pago: e.target.value})}
                            >
                                <option value="Completado">Liquidado</option>
                                <option value="Pendiente">Pendiente</option>
                                <option value="Parcial">Abono Parcial</option>
                            </select>
                        </div>
                   </div>

                   <div className="space-y-1.5">
                        <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Notas de Auditoría</label>
                        <textarea 
                            className="w-full bg-black/40 border border-white/5 rounded-xl p-3 text-white focus:border-emerald-500/50 outline-none transition-all font-medium min-h-[100px] resize-none text-[13px] italic leading-relaxed"
                            value={formData.notas}
                            onChange={e => setFormData({...formData, notas: e.target.value})}
                            placeholder="Detalles adicionales..."
                        />
                   </div>

                   <div className="flex items-center gap-4 pt-4 pb-8">
                        <div className="flex-1 p-3 rounded-xl bg-blue-500/5 border border-blue-500/10 flex items-center gap-3">
                            <ShieldCheck className="w-5 h-5 text-blue-400" />
                            <div>
                                <p className="text-[8px] font-black text-blue-400 uppercase tracking-widest">Validación</p>
                                <p className="text-[9px] font-bold text-slate-500 uppercase italic">Fluxo Audit</p>
                            </div>
                        </div>
                        <button 
                            onClick={handleSave}
                            disabled={!formData.producto_servicio || total <= 0}
                            className="flex-[2] py-3 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 disabled:opacity-30 disabled:cursor-not-allowed text-white font-black uppercase tracking-[0.2em] text-xs rounded-xl shadow-[0_0_20px_rgba(16,185,129,0.2)] transition-all flex items-center justify-center gap-2 active:scale-95"
                        >
                            <span>Ejecutar Transacción</span>
                            <ArrowRight className="w-4 h-4" />
                        </button>
                   </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VentasProPage;

